import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AddPoolParams,
  AddAdminPoolSettings,
  AddUserPoolSettings,
  IAddPoolResponse,
} from "@services/addPoolSettings";
import { ErrorType } from "@components/global/new/Error/Error";
import { useMe } from "@utils/hooks/useMe";
import { AddPooPropsI } from "./AddPoolSetting";
import { getFormValue } from "@utils/getFormValue";
import { useDispatch } from "react-redux";
import { hide } from "redux-modal";
import { MODALS_ENUM } from "@constants/Modals";
import { useUsers } from "@utils/hooks/useUsers";
import { IStoredSetting, SeparatorType } from "@store/me/actionTypes";
import { makeNotifs } from "@utils/makeNotifs";
import useApiRequests from "@services/http/useApiRequest";
import { ALGOS_E } from "@constants/Algos";
import {
  addPoolSetting,
  editPoolSetting,
  setPoolSettings,
} from "@store/me/actions";
import {
  GetAdminSettingsRequest,
  GetUserSettingsRequest,
  IPoolCreds,
} from "@services/getSettings";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { useAuth } from "@utils/hooks/useAuth";
import { RecordResponse } from "@services/http/responses/BaseResponse";
import { dev } from "@utils/dev";
import useTypedRequest from "@utils/hooks/useTypedRequest";
import { getPoolTemplates } from "@services/pooltemplates";
import { usePoolTemplates } from "@utils/hooks/usePoolTemplates";
import { setPoolTemplates } from "@store/poolTemplates/actions";
import { checkPool, convertFormValuesToPoolWithCheck, getPoolsToChange, hasAnyError, isStratumProtocol, MAX_POOLS_COUNT, PoolBase, PoolWithCheck, SettingFormValues, withCheckError } from "@components/Pages/Settings/Settings.service";

const COMMON_PROTOCOL = "stratum+tcp://";


export const formValuesToCreds = (
  f: SettingFormValues
): { name: string; Pools: IPoolCreds[] } => {
  const pools = [
    {
      address: f.address_1,
      login: f.login_1,
      password: f.password_1,
      separator: f.separator_1,
    },
  ];
  if (f.address_2) {
    pools.push({
      address: f.address_2,
      login: f.login_2,
      password: f.password_2,
      separator: f.separator_2,
    });
  }
  if (f.address_3) {
    pools.push({
      address: f.address_3,
      login: f.login_3,
      password: f.password_3,
      separator: f.separator_3,
    });
  }
  return { name: f.name, Pools: pools };
};

interface Elems extends HTMLFormControlsCollection, SettingFormValues { }

interface MyForm extends HTMLFormElement {
  readonly elements: Elems;
}

const hasProtocol = (address: string) => {
  // const regex =
  //   /^(stratum\+tcp:\/\/([\w-]+)(\.([\w-]+))*):(([0-9]{1,4})|([1-5][0-9]{4})|(6[0-4][0-9]{3})|(65[0-4][0-9]{2})|(655[0-2][0-9])|(6553[0-5]))(#xnsub)?$/;
  const regex = /:\/\//;

  return regex.test(address);
};

export const useAddPoolSettingControl = ({
  edit,
  setting,
  onSelectPool,
}: AddPooPropsI) => {

  const requests = useApiRequests();
  const auth = useAuth();

  const notifs = makeNotifs();
  const { onSuccess, onLoader } = useGlobalNotifs();
  const dispatch = useDispatch();

  const formLastElement = useRef<HTMLElement | null>();
  const templates = usePoolTemplates();
  const { request: getPoolTemplatesRequest } = useTypedRequest(getPoolTemplates);

  const [initial, setInitial] = useState(withCheckError(setting?.Pools || []));
  const [confirm, setConfirm] = useState<boolean>(false);
  const [errorField, setErrorField] = useState<ErrorType>();
  const [algo, setAlgo] = useState<ALGOS_E>(setting?.hash || ALGOS_E.SHA256);

  const poolsToChange = useMemo(() => {
    const result: PoolWithCheck[] = [];
    result.push(initial.pool1);
    result.push(initial.pool2);
    result.push(initial.pool3);
    return result;
  }, [initial]);

  useEffect(() => {
    if (!templates || !templates.length) {
      (async () => {
        const res = await getPoolTemplatesRequest();
        if (!res) return;
        dispatch(setPoolTemplates(res.Records));
      })();
    }
  }, []);

  const formHandle = useCallback(async (e: React.FormEvent<MyForm>) => {
    e.preventDefault();
    const elems = e.currentTarget.elements;
    setErrorField(undefined);
    let formValues = getFormValue(elems) as unknown as SettingFormValues;
    if (formValues.name.trim() === "") {
      setErrorField({ name: true });
    }
    const pools = convertFormValuesToPoolWithCheck(formValues);
    if (pools.pool1.isFullEmpty && pools.pool2.isFullEmpty && pools.pool3.isFullEmpty) {
      setErrorField(prev => ({ ...prev, fill: true }));
      return;
    }
    setInitial(pools);
    if (hasAnyError(pools)) {
      setErrorField(prev => ({
        ...prev,
        add_pool_setting__some_errors: true,
      }));
      return;
    }
    // после проверок на ошибки
    if (!confirm) {
      setTimeout(
        () =>
          formLastElement.current && formLastElement.current.scrollIntoView(),
        150
      );
      setErrorField({
        ownError: [notifs.errors.add_pool_setting__check],
      });
      return;
    }
    const params: AddPoolParams = {
      ...formValuesToCreds(formValues),
      hash: algo,
      edit,
      id: setting?.id,
    };
    try {
      onLoader(true);
      // Автоматически добавляем протокол к адресам, если его нет
      params.Pools = params.Pools.map((pool) => ({
        ...pool,
        address:
          pool.address && !hasProtocol(pool.address)
            ? COMMON_PROTOCOL + pool.address
            : pool.address,
      }));

      const request = auth.isAdmin
        ? new AddAdminPoolSettings(params)
        : new AddUserPoolSettings(params);

      const response = await requests.enqueue<IAddPoolResponse>(request);

      if (!response) return;
      onSuccess(notifs.ok.success);

      if (!edit) {
        await updateSettings(response.Record);
      } else {
        const data = {
          ...formValuesToCreds(formValues),
          hash: algo,
          id: setting.id,
        } as IStoredSetting;
        dispatch(editPoolSetting(data));
        onSelectPool(data);
        if (setting.id && typeof setting.id === "number") {
          await updateSettings(setting.id, true);
        }
      }
      dispatch(hide(MODALS_ENUM.NEW_POOL_SETTINGS));
    } finally {
      onLoader(false);
    }

    /*
        let isEmpty = true;
    
        //console.log("useAddPoolSettingControl", formValues);
    
        for (let key in formValues) {
          if (
            !!formValues[key as keyof typeof formValues] &&
            !key.includes("separator")
          )
            isEmpty = false;
        }
    
        if (isEmpty) return setErrorField({ fill: true });
    
        if (!isEmpty && !confirm) {
          setTimeout(
            () =>
              formLastElement.current && formLastElement.current.scrollIntoView(),
            200
          );
          return setErrorField({
            ownError: [notifs.errors.add_pool_setting__check],
          });
        }
    
        const params: AddPoolParams = {
          ...formValuesToCreds(formValues),
          hash: algo,
          edit,
          id: setting?.id,
        };
    
        try {
          onLoader(true);
    
          // Автоматически добавляем протокол к адресам, если его нет
          params.Pools = params.Pools.map((pool) => ({
            ...pool,
            address:
              pool.address && !hasProtocol(pool.address)
                ? COMMON_PROTOCOL + pool.address
                : pool.address,
          }));
    
          const request = me?.is_admin
            ? new AddAdminPoolSettings(params)
            : new AddUserPoolSettings(params);
    
          const response = await requests.enqueue<IAddPoolResponse>(request);
    
          if (!response) return;
          onSuccess(notifs.ok.success);
    
          if (!edit) {
            await updateSettings(response.Record);
          } else {
            const data = {
              ...formValuesToCreds(formValues),
              hash: algo,
              id: setting.id,
            } as IStoredSetting;
            dispatch(editPoolSetting(data));
            onSelectPool(data);
            if (setting.id) {
              await updateSettings(setting.id, true);
            }
          }
          dispatch(hide(MODALS_ENUM.NEW_POOL_SETTINGS));
  } finally {
    onLoader(false);
  }*/
  }, [confirm]);

  const onConfirm = () => setConfirm(!confirm);

  const updateSettings = async (newSettingId: number, isEdit = false) => {
    let settList: IStoredSetting[] = [];
    const request: GetAdminSettingsRequest | GetUserSettingsRequest =
      auth.isAdmin
        ? new GetAdminSettingsRequest()
        : new GetUserSettingsRequest();

    const settings = await requests.enqueue<RecordResponse<IStoredSetting[]>>(
      request
    );

    if (!settings) return;
    const s: IStoredSetting[] = [];
    settings.Record.forEach((setting) => {
      s.push({
        ...setting,
        id: setting.id || 0,
      });
    });
    settList = s;
    dispatch(setPoolSettings(settList));
    // saveCache(settingsCacheKey, settList, 10 * MINUTE); //save for 10 minutes

    const currentSetting = settList.find((set) => set.id == newSettingId);

    if (!currentSetting) {
      console.error("No currentSetting with id = ", newSettingId, settList);
      return;
    }

    // if (isEdit) {
    //   dispatch(editPoolSetting(currentSetting));
    // } else {
    //   dispatch(addPoolSetting(currentSetting));
    // }
    // console.log("currentSetting = ", currentSetting);
    onSelectPool(currentSetting);
  };

  return {
    algo,
    error: errorField,
    isAdmin: auth.isAdmin,
    confirm,
    templates,
    poolsToChange,
    setAlgo,
    formHandle,
    onConfirm,
    setFormLastElement(el: HTMLElement | null) {
      formLastElement.current = el;
    },
  };
};
